import type {IntlShape} from 'react-intl';
import {formatDuration, formatHours} from '../../../forecast-app/admin-tab/templates-page/baseline/baselineTemplateFormat';

type DataLineRow = {
	phaseCount: number | null;
	totalMinutes: number | null;
	roleCount: number | null;
	durationDays: number | null;
	includeDeliverables: boolean | null;
	deliverableCount: number | null;
};

const SEPARATOR = ' · ';

/** "2 phases · 45h · 4 roles · 59 days · 12 weeks · 2 deliverables"; deliverables only when both sides use them. */
export const formatDataLine = (intl: IntlShape, row: DataLineRow, projectUsesDeliverables: boolean): string => {
	const parts = [
		intl.formatMessage({id: 'baseline_templates.insert.data.phases'}, {count: row.phaseCount ?? 0}),
		formatHours(intl, row.totalMinutes ?? 0),
		intl.formatMessage({id: 'baseline_templates.insert.data.roles'}, {count: row.roleCount ?? 0}),
		formatDuration(intl, row.durationDays ?? 0),
	];
	if (projectUsesDeliverables && row.includeDeliverables && (row.deliverableCount ?? 0) > 0) {
		parts.push(intl.formatMessage({id: 'baseline_templates.insert.data.deliverables'}, {count: row.deliverableCount}));
	}
	return parts.join(SEPARATOR);
};

/** Some of a template's lines, as the preview counts them: a phase's, or one of its deliverables'. */
type PreviewLines = {
	timeEstimates?: ReadonlyArray<unknown> | null;
	expenseCount?: number | null;
};

type PreviewPhase = PreviewLines & {deliverables: ReadonlyArray<PreviewLines | null> | null};

type PreviewNoteInput = {
	includeDeliverables: boolean | null;
	deliverableCount: number | null;
	phases: ReadonlyArray<PreviewPhase | null>;
};

/** Whether a phase, or one of its deliverables, holds any time estimate or expense line. */
export const hasLines = (lines: PreviewLines | null): boolean =>
	!!lines && ((lines.timeEstimates ?? []).some(line => line !== null) || (lines.expenseCount ?? 0) > 0);

const presentPhases = (preview: PreviewNoteInput): PreviewPhase[] => preview.phases.flatMap(phase => (phase ? [phase] : []));

const hasNoDeliverable = (preview: PreviewNoteInput, phase: PreviewPhase): boolean =>
	!preview.includeDeliverables || (phase.deliverables ?? []).length === 0;

/**
 * Whether an insert on a deliverables project puts each phase's phase-level lines under a deliverable named after
 * the phase. It does when no inserted line would otherwise sit under a deliverable: none of the template's lines has
 * a deliverable, and every phase that links none, whose lines go under one named after it, is empty.
 */
export const phaseLevelLinesGetPhaseDeliverable = (preview: PreviewNoteInput): boolean =>
	!presentPhases(preview).some(phase =>
		hasNoDeliverable(preview, phase) ? hasLines(phase) : (phase.deliverables ?? []).some(hasLines)
	);

/**
 * How many deliverables an insert creates on a deliverables project: each template deliverable once, however many
 * phases it sits on, plus one named after each phase that has none, and after each phase whose phase-level lines
 * get one of their own.
 */
export const insertedDeliverableCount = (preview: PreviewNoteInput): number => {
	const phases = presentPhases(preview);
	const namedAfterPhase = phaseLevelLinesGetPhaseDeliverable(preview);
	return (
		(preview.includeDeliverables ? preview.deliverableCount ?? 0 : 0) +
		phases.filter(phase => hasNoDeliverable(preview, phase) || (namedAfterPhase && hasLines(phase))).length
	);
};

/** The preview's info box: the rates and dates note, then what happens to deliverables on this project. */
export const previewNotes = (intl: IntlShape, preview: PreviewNoteInput, projectUsesDeliverables: boolean): string[] => {
	const notes = [intl.formatMessage({id: 'baseline_templates.insert.preview.note'})];
	const templateDeliverables = preview.includeDeliverables ? preview.deliverableCount ?? 0 : 0;
	if (!projectUsesDeliverables && templateDeliverables > 0) {
		notes.push(
			intl.formatMessage(
				{id: 'baseline_templates.insert.preview.note_deliverables_skipped'},
				{count: templateDeliverables}
			)
		);
	}
	const phases = presentPhases(preview);
	const phaseGetsDeliverable =
		phases.some(phase => hasNoDeliverable(preview, phase)) ||
		(phaseLevelLinesGetPhaseDeliverable(preview) && phases.some(hasLines));
	if (projectUsesDeliverables && phaseGetsDeliverable) {
		notes.push(intl.formatMessage({id: 'baseline_templates.insert.preview.note_deliverables_added'}));
	}
	return notes;
};
